import React, { useEffect } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import PropTypes from 'prop-types';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material';
import { API, CONTACT_MAX_NOTES_LEN, genderOptions } from '../../../shared/constants/constants';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useDispatch } from 'react-redux';
import { openSnackbar } from '../../AppSnackbar/snackbarSlice';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import MergeIcon from '@mui/icons-material/Merge';
import ArchiveIcon from '@mui/icons-material/Archive';
import MergeContactPersonnelModal from './MergeContactPersonnelModal';
import AlertChangeOrgUnit from '../AlertChangeOrgUnit';
import { normalizeCommunityMemberPayload } from '../../../shared/utils/communityMemberNormalization';
import {
  getCommunityMembersByCaseInsensitiveNameAndOrganization,
  getExpertsByCaseInsensitiveNameAndUnit,
  getJournalistsByCaseInsensitiveNameAndOrg,
} from '../contactsRequest';
import { useArchiveContact } from '../Hooks/useArchiveContact';
import { ComboBoxCreatableOutlet } from '../../Cells/CellTypes';
import { useQueryClient, useMutation, useQuery } from '@tanstack/react-query';
import { normalizeExpertPayload } from '../../../utils/expertNormalization';
import { normalizeJournalistPayload } from '../../../shared/functions/normalizeJournalistPayload';

const useStyles = makeStyles(({ palette, tabColor }) => ({
  cellInput: {
    display: 'flex',
    flexDirection: 'column',
  },
  smCellInput: {
    width: 80,
  },
  smCellInputContainer: {
    maxWidth: 80,
  },
  groupLabel: {
    background: ({ tabColor }) => `${tabColor} !important`,
    color: `${palette.primary.contrastText} !important`,
    fontWeight: '700 !important',
  },
  dialog: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '25rem',
    width: '30rem',
    overflow: 'hidden',
  },
  charCountText: {
    color: 'rgba(0, 0, 0, 0.6)',
  },
}));

const getCurrentOrg = (orgs, selectedOrg) => {
  if (!selectedOrg) {
    return undefined;
  }
  return orgs.find((org) => org._id === selectedOrg);
};

const getSelectedExpertUnits = (selectedContact, units) => {
  if (!selectedContact?.units || !Array.isArray(units)) {
    return [];
  }
  return units.filter((unit) => selectedContact.units.includes(unit._id));
};

const getPrefillOrgUnit = ({ isEditing, category, selectedContact, orgs, units }) => {
  if (!isEditing) {
    return category === 'Experts' ? [] : undefined;
  }

  switch (category) {
    case 'Journalists':
      return orgs.find((org) => org._id === selectedContact?.Org?._id);
    case 'Community Members':
      return orgs.find((org) => org._id === selectedContact?.Organization?._id);
    case 'Experts':
      return getSelectedExpertUnits(selectedContact, units);
    default:
      return units.find((unit) => unit._id === selectedContact?.unit);
  }
};

const concatCustomFieldsYupSchema = (baseSchema, customFieldConfigs) => {
  if (!customFieldConfigs) {
    return baseSchema;
  }

  const customFields = {};
  customFieldConfigs.forEach((config) => {
    customFields[config.fieldKey] = yup.string().nullable();
  });

  return baseSchema.concat(yup.object().shape(customFields));
};

const getCategorySchemaConfig = (category, customFieldConfigs) => {
  let schema;
  let url;

  switch (category) {
    case 'Journalists':
      url = '/journalist';
      schema = yup.object().shape({
        name: yup.string().required(),
        email: yup.string().email(),
        phone: yup.string().max(32),
        cell: yup.string().max(32),
        notes: yup.string().min(0).max(CONTACT_MAX_NOTES_LEN),
        Org: yup.string().nullable(),
      });
      break;
    case 'Community Members':
      url = '/community-member';
      schema = yup.object().shape({
        name: yup.string().required(),
        email: yup.string().email(),
        phone: yup.string().max(32),
        cell: yup.string().max(32),
        notes: yup.string().min(0).max(CONTACT_MAX_NOTES_LEN),
        Organization: yup.string().nullable(),
      });
      break;
    default:
      url = '/expert';
      schema = yup.object().shape({
        name: yup.string().required(),
        email: yup.string().email(),
        phone: yup.string().max(32),
        units: yup.array().of(
          yup.lazy((value) => {
            if (typeof value === 'string') {
              return yup.string().required();
            }
            return yup.object().shape({
              name: yup.string().required(),
            });
          })
        ),
        language: yup.string(),
        gender: yup.string().nullable(),
        notes: yup.string().min(0).max(CONTACT_MAX_NOTES_LEN),
      });
      break;
  }

  return {
    schema: concatCustomFieldsYupSchema(schema, customFieldConfigs),
    url,
  };
};

const normalizeContactData = (category, data) => {
  switch (category) {
    case 'Community Members':
      return normalizeCommunityMemberPayload(data);
    case 'Journalists':
      return normalizeJournalistPayload(data);
    default:
      return data;
  }
};

const getDialogValueReset = (category) => {
  switch (category) {
    case 'Experts':
      return {
        name: '',
        phone: '',
        email: '',
        unit: '',
        language: '',
        gender: '',
        _id: '',
      };
    case 'Community Members':
      return {
        name: '',
        phone: '',
        cell: '',
        notes: '',
        email: '',
        Organization: '',
        _id: '',
      };
    default:
      return {
        name: '',
        phone: '',
        cell: '',
        notes: '',
        email: '',
        Org: '',
        _id: '',
      };
  }
};

const getModalTitle = (category, isEditing) => {
  const currentCategory = category.slice(0, -1);
  return isEditing ? `Edit ${currentCategory}` : `Add New ${currentCategory}`;
};

const getDuplicateWarningMessage = (count, category) => {
  return `${count} contact personnel(s) with the same Name and Organization/Unit already exists. Please check if you are trying to add a ${category
    .toLowerCase()
    .slice(0, -1)} that already exists.`;
};

const getDuplicateQueryPromise = (category, params) => {
  switch (category) {
    case 'Journalists':
      return getJournalistsByCaseInsensitiveNameAndOrg(params.name, params.Org ? params.Org : '');
    case 'Community Members':
      return getCommunityMembersByCaseInsensitiveNameAndOrganization(
        params.name,
        params.Organization ? params.Organization : ''
      );
    default:
      return getExpertsByCaseInsensitiveNameAndUnit(params.name, params.unit ? params.unit : '');
  }
};

const updateLinkedContactValue = ({
  category,
  response,
  variables,
  dialogValue,
  value,
  setValue,
  callback,
  col,
  currentOrg,
  prefilledOrgUnit,
}) => {
  dialogValue._id = response.data.id;

  switch (category) {
    case 'Experts':
      dialogValue.Org = response.data.Org;
      value.push({ _id: response.data.id, name: response.data.name });
      callback(value, col);
      return;
    case 'Community Members': {
      const Organization = (currentOrg || prefilledOrgUnit)?._id ?? '';
      dialogValue.Organization = Organization;
      setValue({ _id: response.data.id, name: response.data.name, Organization });
      callback(dialogValue, col);
      return;
    }
    default: {
      const Org = (currentOrg || prefilledOrgUnit)?._id ?? '';
      dialogValue.Org = Org;
      setValue({ _id: response.data.id, name: response.data.name, Org });
      callback(dialogValue, col);
    }
  }
};

const CategorySpecificFields = ({
  category,
  isInContactPage,
  control,
  currentOrg,
  prefilledOrgUnit,
  orgs,
  units,
  errors,
  classes,
  isEditing,
  selectedContact,
  setPrefilledOrgUnit,
  setRefreshOrgs,
  setChangeWarning,
  renderCustomFields,
}) => {
  const safeOrgs = Array.isArray(orgs) ? orgs : [];
  const safeUnits = Array.isArray(units) ? units : [];
  const expertUnitsValue = Array.isArray(prefilledOrgUnit) ? prefilledOrgUnit : [];

  const renderCellField = () => (
    <Controller
      name="cell"
      control={control}
      defaultValue={isEditing ? selectedContact.cell : ''}
      render={({ field }) => (
        <TextField
          {...field}
          label="Cell"
          variant="outlined"
          className={classes.cellInput}
          error={errors.phone}
          helperText={errors.phone?.message}
          data-cy="cell-input-CombinedContactPersonnelModal"
        />
      )}
    />
  );

  const renderJournalistOrgField = () => {
    if (isInContactPage) {
      return (
        <Controller
          name="Org"
          control={control}
          defaultValue={currentOrg ? currentOrg._id : prefilledOrgUnit?._id}
          render={({ field }) => (
            <ComboBoxCreatableOutlet
              options={safeOrgs}
              val={currentOrg ? currentOrg._id : prefilledOrgUnit?._id}
              col={'outlet'}
              textFieldVariant={'outlined'}
              disableTooltip={true}
              callback={(data) => {
                setPrefilledOrgUnit(data);
                field.onChange(data?._id);
                setRefreshOrgs();
                if (isEditing) setChangeWarning(true);
              }}
            />
          )}
        />
      );
    }

    return (
      <Controller
        name="Org"
        control={control}
        defaultValue={currentOrg ? currentOrg._id : prefilledOrgUnit?._id}
        render={({ field }) => (
          <Autocomplete
            {...field}
            disablePortal
            clearOnEscape
            openOnFocus
            autoHighlight
            options={safeOrgs}
            getOptionLabel={(option) => option.name}
            value={currentOrg || prefilledOrgUnit}
            onChange={(_, data) => {
              setPrefilledOrgUnit(data);
              field.onChange(data ? data._id : data);
              if (isEditing) setChangeWarning(true);
            }}
            data-cy="org-autoComplete-CombinedContactPersonnelModal"
            renderInput={(params) => (
              <TextField
                {...params}
                label="Organization"
                variant="outlined"
                className={classes.cellInput}
                error={errors.Org}
                helperText={errors.Org?.message}
                data-cy="org-input-CombinedContactPersonnelModal"
              />
            )}
          />
        )}
      />
    );
  };

  switch (category) {
    case 'Journalists':
      return (
        <>
          {renderJournalistOrgField()}
          {renderCellField()}
          {renderCustomFields()}
        </>
      );
    case 'Experts':
      return (
        <>
          <Controller
            name="units"
            control={control}
            defaultValue={isEditing ? prefilledOrgUnit : []}
            render={({ field }) => (
              <Autocomplete
                {...field}
                multiple
                disablePortal
                clearOnEscape
                openOnFocus
                autoHighlight
                options={safeUnits}
                getOptionLabel={(option) => option.name}
                value={expertUnitsValue}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => {
                    return (
                      <Chip
                        sx={{ 'MuiChip-label': { fontSize: '12px' } }}
                        size="small"
                        variant="outlined"
                        label={option.name}
                        {...getTagProps({ index })}
                      />
                    );
                  })
                }
                onChange={(_, data) => {
                  setPrefilledOrgUnit(data || []);
                  field.onChange(data ? data.map((d) => d._id) : []);
                  if (isEditing) setChangeWarning(true);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Units"
                    variant="outlined"
                    className={classes.cellInput}
                    error={errors.unit}
                    helperText={errors.unit?.message}
                    data-cy="unit-input-CombinedContactPersonnelModal"
                  />
                )}
              />
            )}
          />
          <Controller
            name="language"
            control={control}
            defaultValue={isEditing ? selectedContact.language : ''}
            render={({ field }) => (
              <TextField
                {...field}
                label="Language"
                variant="outlined"
                className={classes.cellInput}
                error={errors.language}
                helperText={errors.language?.message}
                data-cy="language-input-CombinedContactPersonnelModal"
              />
            )}
          />
          <Controller
            name="gender"
            control={control}
            defaultValue={isEditing ? selectedContact?.gender : ''}
            render={({ field }) => (
              <Autocomplete
                {...field}
                disablePortal
                clearOnEscape
                openOnFocus
                autoHighlight
                options={genderOptions}
                getOptionLabel={(option) => option.name ?? option}
                isOptionEqualToValue={(option, value) => option.name === value}
                onChange={(_, data) => {
                  field.onChange(data ? data.name : data);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Gender"
                    variant="outlined"
                    className={classes.cellInput}
                    error={errors.gender}
                    helperText={errors.gender?.message}
                    data-cy="gender-input-CombinedContactPersonnelModal"
                  />
                )}
              />
            )}
          />
          {renderCustomFields()}
        </>
      );
    default:
      return (
        <>
          <Controller
            name="Organization"
            control={control}
            defaultValue={currentOrg ? currentOrg._id : prefilledOrgUnit?._id}
            render={({ field }) => (
              <Autocomplete
                {...field}
                disablePortal
                clearOnEscape
                openOnFocus
                autoHighlight
                options={safeOrgs}
                getOptionLabel={(option) => option.name}
                value={currentOrg || prefilledOrgUnit}
                onChange={(_, data) => {
                  setPrefilledOrgUnit(data);
                  field.onChange(data ? data._id : data);
                  if (isEditing) setChangeWarning(true);
                }}
                data-cy="organization-autoComplete-CombinedContactPersonnelModal"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Organization"
                    variant="outlined"
                    className={classes.cellInput}
                    error={errors.Organization}
                    helperText={errors.Organization?.message}
                    data-cy="organization-input-CombinedContactPersonnelModal"
                  />
                )}
              />
            )}
          />
          {renderCellField()}
          {renderCustomFields()}
        </>
      );
  }
};

CategorySpecificFields.propTypes = {
  category: PropTypes.oneOf(['Journalists', 'Community Members', 'Experts']).isRequired,
  isInContactPage: PropTypes.bool,
  control: PropTypes.object.isRequired,
  currentOrg: PropTypes.shape({
    _id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
  }),
  prefilledOrgUnit: PropTypes.oneOfType([
    PropTypes.shape({
      _id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
    }),
    PropTypes.arrayOf(
      PropTypes.shape({
        _id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        name: PropTypes.string,
      })
    ),
  ]),
  orgs: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
    })
  ),
  units: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
    })
  ),
  errors: PropTypes.object.isRequired,
  classes: PropTypes.shape({
    cellInput: PropTypes.string,
  }).isRequired,
  isEditing: PropTypes.bool,
  selectedContact: PropTypes.shape({
    cell: PropTypes.string,
    language: PropTypes.string,
    gender: PropTypes.string,
  }),
  setPrefilledOrgUnit: PropTypes.func.isRequired,
  setRefreshOrgs: PropTypes.func,
  setChangeWarning: PropTypes.func.isRequired,
  renderCustomFields: PropTypes.func.isRequired,
};

const CombinedContactPersonnelModal = (props) => {
  // Props for adding a new contact
  const {
    openAddContact,
    setOpenAddContact,
    orgs,
    setRefreshContacts,
    setRefreshOrgs,
    category,
    units,
    customFieldConfigs,
  } = props;
  // Props for editing a contact on Contacts page
  const { isEditing, selectedContact } = props;
  // Props for entering from MediaInteractions/ServicePage/EngagementLog
  const { isInContactPage, col, dialogValue, setDialogValue, value, setValue, callback, selectedOrg } = props;
  // Props for entering from AutoTracker
  const { isInAutoTrackerPage } = props;

  const currentOrg = getCurrentOrg(orgs, selectedOrg);

  // Prop for merging contacts
  const { contacts } = props;

  const [prefilledOrgUnit, setPrefilledOrgUnit] = React.useState(() =>
    getPrefillOrgUnit({ isEditing, category, selectedContact, orgs, units })
  );

  const [openMergeContact, setOpenMergeContact] = React.useState(false);
  const [changeWarning, setChangeWarning] = React.useState(false);

  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const archiveContactMutation = useArchiveContact({
    contactId: selectedContact?._id,
    category: category,
    isArchiving: true,
    setRefetchContacts: setRefreshContacts,
  });

  const duplicateCheckParamsRef = React.useRef(null);

  const duplicateCheckQuery = useQuery({
    queryKey: ['contactPersonnelDuplicates', category, selectedContact?._id],
    queryFn: async () => {
      const params = duplicateCheckParamsRef.current;
      if (!params) {
        return { data: [] };
      }
      return getDuplicateQueryPromise(category, params);
    },
    enabled: false,
    staleTime: 0,
  });

  const createContactMutation = useMutation({
    mutationFn: (data) => {
      const payload = category === 'Experts' ? normalizeExpertPayload(data) : data;
      return API.post(url, payload);
    },
    onSuccess: async (response, variables) => {
      if (response.status !== 201) {
        alert(response.statusText);
        return;
      }
      dispatch(openSnackbar({ severity: 'success', message: 'Contact Personnel Created!' }));
      if (isInContactPage) {
        setRefreshContacts(true);
      }
      await queryClient.invalidateQueries({ queryKey: ['dropdown-table'] });

      if (!isInContactPage && !isInAutoTrackerPage) {
        updateLinkedContactValue({
          category,
          response,
          variables,
          dialogValue,
          value,
          setValue,
          callback,
          col,
          currentOrg,
          prefilledOrgUnit,
        });
      } else if (isInAutoTrackerPage) {
        callback(dialogValue, col);
      }
    },
    onError: (error) => {
      console.log(JSON.stringify(error));
      alert(`post failed - ${error}`);
    },
  });

  const updateContactMutation = useMutation({
    mutationFn: (data) => {
      const payload = category === 'Experts' ? normalizeExpertPayload(data) : data;
      return API.put(`${url}/${selectedContact._id}`, payload);
    },
    onSuccess: async (response) => {
      if (response.status !== 200) {
        alert(response.statusText);
        return;
      }
      dispatch(openSnackbar({ severity: 'success', message: 'Contact Personnel Updated!' }));
      setRefreshContacts(true);
      await queryClient.invalidateQueries({ queryKey: ['dropdown-table'] });
      handleClose();
    },
    onError: (error) => {
      alert(`put failed - ${error}`);
    },
  });

  const { schema, url } = getCategorySchemaConfig(category, customFieldConfigs);

  const {
    handleSubmit,
    formState: { errors },
    control,
    reset,
    watch,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      name: '',
      email: null,
      phone: null,
      Org: null,
      Organization: null,
      unit: null,
      units: [],
      cell: null,
      notes: null,
      language: null,
      gender: null,
    },
  });
  const classes = useStyles();

  const checkDuplicates = async (data) => {
    try {
      duplicateCheckParamsRef.current = data;
      const { data: response } = await duplicateCheckQuery.refetch();
      const responseData = Array.isArray(response?.data) ? response.data : [];

      if (responseData.length > 0) {
        dispatch(
          openSnackbar({
            severity: 'warning',
            message: getDuplicateWarningMessage(responseData.length, category),
          })
        );
        return true;
      }
      return false;
    } catch (err) {
      alert(err);
      return false;
    }
  };

  const checkDuplicatesButExcludeSelected = async (data) => {
    try {
      duplicateCheckParamsRef.current = data;
      const { data: response } = await duplicateCheckQuery.refetch();
      const responseData = Array.isArray(response?.data) ? response.data : [];
      const filteredDuplicates = responseData.filter((oneDuplicateContact) => {
        return oneDuplicateContact._id !== selectedContact._id;
      });

      if (filteredDuplicates.length > 0) {
        dispatch(
          openSnackbar({
            severity: 'warning',
            message: getDuplicateWarningMessage(filteredDuplicates.length, category),
          })
        );
        return true;
      }
      return false;
    } catch (err) {
      alert(err);
      return false;
    }
  };

  const handleArchive = async () => {
    try {
      await archiveContactMutation.mutateAsync();
      handleClose();
    } catch (error) {
      console.log(error);
    }
  };

  const handleUpdate = async (data) => {
    const normalizedData = normalizeContactData(category, data);

    if (await checkDuplicatesButExcludeSelected(normalizedData)) return;

    return updateContactMutation.mutateAsync(normalizedData);
  };

  const handleClose = () => {
    reset();
    setOpenAddContact(false);
    setChangeWarning(false);
    if (!isInContactPage) {
      setDialogValue(getDialogValueReset(category));
    }
  };

  const forSubmit = async (data, category) => {
    const normalizedData = normalizeContactData(category, data);
    if (isEditing) {
      await handleUpdate(normalizedData);
      return;
    }
    // first, check if the contact with the same name and Org already exists
    if (await checkDuplicates(normalizedData)) return;

    // if not, create a new contact
    createContactMutation.mutate(normalizedData);
    reset();
    handleClose();
  };

  useEffect(() => {
    reset({
      name: selectedContact?.name,
      email: selectedContact?.email,
      phone: selectedContact?.phone,
    });
    if (selectedContact) {
      setPrefilledOrgUnit(getPrefillOrgUnit({ isEditing, category, selectedContact, orgs, units }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing, selectedContact, category, units, reset]);

  useEffect(() => {
    if (!isInContactPage) {
      reset({
        name: dialogValue.name,
      });
    }
  }, [dialogValue?.name, isInContactPage, reset]);

  const renderCustomFields = () => {
    if (customFieldConfigs) {
      return (
        <>
          {customFieldConfigs.map((customFieldConfig) => (
            <Controller
              key={customFieldConfig.fieldKey}
              name={customFieldConfig.fieldKey}
              control={control}
              defaultValue={
                selectedContact?.[customFieldConfig.fieldKey] ? selectedContact[customFieldConfig.fieldKey] : ''
              }
              render={({ field }) => (
                <TextField
                  {...field}
                  label={customFieldConfig.label}
                  data-cy={`combined-personnel-modal-textfield-custom-field-${customFieldConfig.label}`}
                  variant="outlined"
                  className={classes.cellInput}
                />
              )}
            />
          ))}
        </>
      );
    }
    return null;
  };

  const handleFormSubmit = (event) => {
    if (event) {
      if (typeof event.preventDefault === 'function') {
        event.preventDefault();
      }
      if (typeof event.stopPropagation === 'function') {
        event.stopPropagation();
      }
    }
    return handleSubmit((data) => forSubmit(data, category))(event);
  };

  return (
    <Dialog
      open={openAddContact}
      onClose={handleClose}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
      }}
    >
      <form onSubmit={handleFormSubmit} data-cy="form-CombinedContactPersonnelModal">
        <DialogTitle>{getModalTitle(category, isEditing)}</DialogTitle>
        <DialogContent className={classes.dialog}>
          <Stack
            spacing={2}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              padding: '1.5rem',
            }}
          >
            <Controller
              name="name"
              control={control}
              defaultValue={() => {
                if (isEditing) return selectedContact.name;
                if (!isInContactPage) return dialogValue.name;
                return '';
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Name"
                  variant="outlined"
                  className={classes.cellInput}
                  error={!!errors.name}
                  helperText={errors.name ? errors.name.message : ''}
                  data-cy="name-input-CombinedContactPersonnelModal"
                />
              )}
            />

            <Controller
              name="email"
              control={control}
              defaultValue={isEditing ? selectedContact.email : ''}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Email"
                  variant="outlined"
                  className={classes.cellInput}
                  error={errors.email}
                  helperText={errors.email?.message}
                  data-cy="email-input-CombinedContactPersonnelModal"
                />
              )}
            />

            <Controller
              name="phone"
              control={control}
              defaultValue={isEditing ? selectedContact.phone : ''}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Phone"
                  variant="outlined"
                  className={classes.cellInput}
                  error={errors.phone}
                  helperText={errors.phone?.message}
                  data-cy="phone-input-CombinedContactPersonnelModal"
                />
              )}
            />

            <CategorySpecificFields
              category={category}
              isInContactPage={isInContactPage}
              control={control}
              currentOrg={currentOrg}
              prefilledOrgUnit={prefilledOrgUnit}
              orgs={orgs}
              units={units}
              errors={errors}
              classes={classes}
              isEditing={isEditing}
              selectedContact={selectedContact}
              setPrefilledOrgUnit={setPrefilledOrgUnit}
              setRefreshOrgs={setRefreshOrgs}
              setChangeWarning={setChangeWarning}
              renderCustomFields={renderCustomFields}
            />

            <br />
            <Controller
              name="notes"
              control={control}
              defaultValue={isEditing ? selectedContact.notes : ''}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Notes"
                  multiline
                  minRows={2}
                  maxRows={5}
                  variant="outlined"
                  inputProps={{ maxLength: CONTACT_MAX_NOTES_LEN }}
                  helperText={
                    <Box className={classes.charCountText}>{`${
                      watch('notes').length
                    }/${CONTACT_MAX_NOTES_LEN} character(s)`}</Box>
                  }
                  className={classes.cellInput}
                  error={errors.notes}
                  data-cy="notes-input-CombinedContactPersonnelModal"
                />
              )}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} data-cy="cancel-button-CombinedContactPersonnelModal">
            Cancel
          </Button>
          {isEditing && (
            <Button
              onClick={() => setOpenMergeContact(true)}
              startIcon={<MergeIcon />}
              data-cy="merge-button-CombinedContactPersonnelModal"
            >
              Merge
            </Button>
          )}
          {isEditing && (
            <Button
              onClick={() => handleArchive()}
              startIcon={<ArchiveIcon />}
              data-cy="archive-button-CombinedContactPersonnelModal"
            >
              Archive
            </Button>
          )}
          {isEditing && (
            <Button type="submit" startIcon={<SyncAltIcon />} data-cy="update-button-CombinedContactPersonnelModal">
              Update
            </Button>
          )}
          {!isEditing && (
            <Button type="submit" startIcon={<AddCircleIcon />} data-cy="add-button-CombinedContactPersonnelModal">
              Add
            </Button>
          )}
        </DialogActions>
      </form>

      <MergeContactPersonnelModal
        openMergeContact={openMergeContact}
        setOpenMergeContact={setOpenMergeContact}
        selectedContact={selectedContact}
        category={category}
        contacts={contacts}
        orgs={orgs}
        units={units}
        setRefreshContacts={setRefreshContacts}
        setOpenAddContact={setOpenAddContact}
      />
      <AlertChangeOrgUnit open={changeWarning} setOpen={setChangeWarning} category={category} />
    </Dialog>
  );
};

export default CombinedContactPersonnelModal;

CombinedContactPersonnelModal.propTypes = {
  openAddContact: PropTypes.bool,
  setOpenAddContact: PropTypes.func,
  orgs: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
    })
  ),
  setRefreshContacts: PropTypes.func,
  setRefreshOrgs: PropTypes.func,
  category: PropTypes.oneOf(['Journalists', 'Community Members', 'Experts']).isRequired,
  units: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
    })
  ),
  customFieldConfigs: PropTypes.arrayOf(
    PropTypes.shape({
      fieldKey: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ),
  isEditing: PropTypes.bool,
  selectedContact: PropTypes.shape({
    _id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    email: PropTypes.string,
    phone: PropTypes.string,
    cell: PropTypes.string,
    notes: PropTypes.string,
    language: PropTypes.string,
    gender: PropTypes.string,
    unit: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    units: PropTypes.array,
    Org: PropTypes.shape({
      _id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
    }),
    Organization: PropTypes.shape({
      _id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
    }),
  }),
  isInContactPage: PropTypes.bool,
  col: PropTypes.string,
  dialogValue: PropTypes.shape({
    _id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    phone: PropTypes.string,
    cell: PropTypes.string,
    notes: PropTypes.string,
    email: PropTypes.string,
    Org: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    Organization: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    unit: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    language: PropTypes.string,
    gender: PropTypes.string,
  }),
  setDialogValue: PropTypes.func,
  value: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  setValue: PropTypes.func,
  callback: PropTypes.func,
  selectedOrg: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isInAutoTrackerPage: PropTypes.bool,
  contacts: PropTypes.array,
};
